import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { realtimeService } from '../services/realtimeService';
import { useAuth } from './AuthContext';

const RealtimeContext = createContext({});

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within RealtimeProvider');
  }
  return context;
};

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);
  const [lastUpdate, setLastUpdate] = useState({
    projects: null,
    phases: null,
    calculations: null,
    users: null,
    taxSettings: null
  });

  // Notify listeners of updates
  const notifyUpdate = useCallback((type, payload) => {
    setLastUpdate(prev => ({
      ...prev,
      [type]: {
        timestamp: Date.now(),
        event: payload?.eventType,
        data: payload?.new || payload?.old
      }
    }));
  }, []);

  // Subscribe to projects
  const subscribeToProjects = useCallback(() => {
    const channel = realtimeService?.subscribeToProjects((payload) => {
      notifyUpdate('projects', payload);
    });
    return channel;
  }, [notifyUpdate]);

  // Subscribe to phases
  const subscribeToPhases = useCallback((projectId = null) => {
    const channel = projectId
      ? realtimeService?.subscribeToPhases(projectId, (payload) => {
          notifyUpdate('phases', payload);
        })
      : realtimeService?.subscribeToAllPhases((payload) => {
          notifyUpdate('phases', payload);
        });
    return channel;
  }, [notifyUpdate]);

  // Subscribe to calculations
  const subscribeToCalculations = useCallback(() => {
    const channel = realtimeService?.subscribeToCalculations((payload) => {
      notifyUpdate('calculations', payload);
    });
    return channel;
  }, [notifyUpdate]);

  // Subscribe to user profiles
  const subscribeToUsers = useCallback(() => {
    const channel = realtimeService?.subscribeToUserProfiles((payload) => {
      notifyUpdate('users', payload);
    });
    return channel;
  }, [notifyUpdate]);

  // Subscribe to tax settings
  const subscribeToTaxSettings = useCallback(() => {
    const channel = realtimeService?.subscribeToTaxSettings((payload) => {
      notifyUpdate('taxSettings', payload);
    });
    return channel;
  }, [notifyUpdate]);

  // Subscribe to IRRF brackets
  const subscribeToIrrfBrackets = useCallback(() => {
    const channel = realtimeService?.subscribeToIrrfBrackets((payload) => {
      notifyUpdate('taxSettings', payload);
    });
    return channel;
  }, [notifyUpdate]);

  // Cleanup all subscriptions
  useEffect(() => {
    return () => {
      realtimeService?.unsubscribeAll(channels);
    };
  }, [channels]);

  const value = {
    lastUpdate,
    subscribeToProjects,
    subscribeToPhases,
    subscribeToCalculations,
    subscribeToUsers,
    subscribeToTaxSettings,
    subscribeToIrrfBrackets,
    unsubscribe: realtimeService?.unsubscribe,
    unsubscribeAll: realtimeService?.unsubscribeAll
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
    </RealtimeContext.Provider>
  );
};

export default RealtimeContext;
